import {
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  CheckCircle as CheckCircleIcon,
  Event as EventIcon,
  LocalFireDepartment as LocalFireDepartmentIcon,
  MusicNote as MusicNoteIcon,
} from "@mui/icons-material";
import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardActionArea,
  CircularProgress,
  Container,
  Divider,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router-dom";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { QRCodeSVG } from "qrcode.react";

import CharCounter from "../../components/CharCounter";
import ChecklistDocumentosUpload from "../../components/ChecklistDocumentosUpload";
import NumeroProcessoField, {
  ehNumeroInformadoValido,
  montarNumeroProcessoPreview,
} from "../../components/NumeroProcessoField";
import autorizacoesService from "../../services/autorizacoes";
import pdfLayoutService from "../../services/pdfLayout";
import authService from "../../services/auth";
import licencaHeaderImg from "../../assets/images/licenca-header.png";
import licencaFooterImg from "../../assets/images/licenca-footer.png";
import {
  formatCpfCnpj,
  formatDateToShow,
  validateCpfCnpj,
} from "../../utils/formatters";
import {
  parseAssinaturaSecretarioResponse,
  resolverFormatoImagemAssinatura,
} from "../../utils/assinaturaSecretario";
import {
  obterCodigoTipoAutorizacao,
  tipoAutorizacaoEnum,
} from "../../utils/constants";
import { getApiErrorMessage } from "../../utils/getApiErrorMessage";
import { CLASSIFICACAO_USO_FOGO_CONTROLADO } from "../../utils/usoFogoControladoClassificacao";
import {
  html2canvasForPdf,
  PDF_PAGE_HEIGHT_MM,
  PDF_PAGE_WIDTH_MM,
  addSlicedContentImage,
  canvasHeightInMm,
  canvasToJpegDataUrl,
  getContentPaging,
} from "../../utils/pagedHtmlPdf";

const TIPO_ICONS = {
  0: <MusicNoteIcon sx={{ fontSize: 32 }} />,
  1: <LocalFireDepartmentIcon sx={{ fontSize: 32 }} />,
};

const TIPO_DESC = {
  0: "Autorização para uso de equipamento de som em eventos públicos ou privados.",
  1: CLASSIFICACAO_USO_FOGO_CONTROLADO.descricaoAtividade,
};

const TIPO_USO_FOGO_CONTROLADO = 1;

const steps = [
  "Tipo de Autorização",
  "Dados do Evento",
  "Documentos",
  "Identificação e Emissão",
];

const NOME_MAX = 100;
const OBS_MAX = 1000;
const NOME_INTERESSADO_MAX = 150;
const ENDERECO_MAX = 300;
const CONDICIONANTES_MAX = 3000;
const OBSERVACOES_EMISSAO_MAX = 1000;

const IMPORTANTE_TEXTO =
  "ESTA LICENÇA NÃO DISPENSA E NEM SUBSTITUI QUAISQUER OUTROS TIPOS DE CERTIDÕES, ALVARÁS, LICENÇAS OU AUTORIZAÇÕES DE QUALQUER NATUREZA, EXIGIDOS PARA O EXERCÍCIO DA ATIVIDADE";

const NIVEIS_PRESSAO_SONORA = [
  { categoria: "Áreas de sítios e fazendas (rurais)", diurno: "40", noturno: "35" },
  { categoria: "Área estritamente residencial urbana", diurno: "50", noturno: "45" },
  { categoria: "Área mista, predominantemente residencial", diurno: "55", noturno: "50" },
  { categoria: "Área mista, com vocação comercial e administrativa", diurno: "60", noturno: "55" },
  { categoria: "Área mista, com vocação recreacional e cultural", diurno: "65", noturno: "55" },
  { categoria: "Área predominantemente industrial", diurno: "70", noturno: "60" },
];

const tipoAutorizacaoLabel = (valor) => {
  const item = tipoAutorizacaoEnum.find((t) => t.value === valor);
  return item ? item.label : "Autorização";
};

const CONDICIONANTES_PADRAO =
  "1- É proibido perturbar o sossego e o bem-estar públicos com ruídos ou vibrações de qualquer natureza que ultrapassem os níveis legalmente previstos para os diferentes horários e zonas de uso.\n\n" +
  "2- O descumprimento das condicionantes da presente Autorização implicará no seu cancelamento, de acordo com a Resolução CONAMA nº 237/97, bem como na aplicação das penalidades previstas na legislação ambiental, sem prejuízo da obrigação de reparar quaisquer danos ambientais causados;\n\n" +
  "3- A inobservância do limite tolerado poderá incorrer em multas e demais sanções previstas nas leis Nº 002/2014 (Código de Posturas do Município de Novo Oriente); Lei 848/2021 (Política Municipal do Meio Ambiente); Lei 9605/98 (Lei de Crimes Ambientais) e Lei 13.711/2005 e Decreto 34.704/2022 (Medidas de combate à poluição sonora gerada por estabelecimentos comerciais e por veículos no Estado do Ceará e dá outras providências).";

function CriarAutorizacaoIndependente() {
  const theme = useTheme();
  const navigate = useNavigate();

  const headerRef = useRef(null);
  const contentRef = useRef(null);
  const footerRef = useRef(null);
  const tabelaRef = useRef(null);
  const qrRef = useRef(null);

  // Stepper
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState("");
  const [emitindo, setEmitindo] = useState(false);
  const [sucesso, setSucesso] = useState(false);
  const [novaAutorizacaoId, setNovaAutorizacaoId] = useState(null);
  const [solicitacaoIdParaPdf, setSolicitacaoIdParaPdf] = useState(null);

  // Step 0
  const [tipoAutorizacao, setTipoAutorizacao] = useState("");
  const [checklist, setChecklist] = useState(null);
  const [loadingChecklist, setLoadingChecklist] = useState(false);

  // Step 1 — dados do evento
  const [form, setForm] = useState({
    nomeEvento: "",
    dataEvento: "",
    horaEvento: "",
    localEvento: "",
    observacoes: "",
  });

  // Step 2 — documentos
  const [documentosFiles, setDocumentosFiles] = useState({});
  const [docErros, setDocErros] = useState({});

  // Step 3 — interessado + emissão
  const [nomeRazaoSocial, setNomeRazaoSocial] = useState("");
  const [cpf, setCpf] = useState("");
  const [cpfErro, setCpfErro] = useState("");
  const [endereco, setEndereco] = useState("");
  const [horaInicio, setHoraInicio] = useState("");
  const [horaFim, setHoraFim] = useState("");
  const [observacoesEmissao, setObservacoesEmissao] = useState("");
  const [condicionantes, setCondicionantes] = useState(CONDICIONANTES_PADRAO);
  const [numeroInformado, setNumeroInformado] = useState("");
  const anoProcesso = new Date().getFullYear();

  // PDF layout
  const [headerImgSrc, setHeaderImgSrc] = useState(null);
  const [footerImgSrc, setFooterImgSrc] = useState(null);
  const [assinaturaDataUrl, setAssinaturaDataUrl] = useState(null);
  const [assinaturaMensagem, setAssinaturaMensagem] = useState(null);
  const [, setUsuarioAtual] = useState(null);

  const hoje = useMemo(() => new Date().toISOString().slice(0, 10), []);

  // Carrega checklist quando tipo muda
  useEffect(() => {
    if (tipoAutorizacao === "") {
      setChecklist(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingChecklist(true);
        setError("");
        const data = await autorizacoesService.getChecklistPorTipo(tipoAutorizacao);
        if (cancelled) return;
        if (!data) {
          setChecklist(null);
          setError("Nenhum checklist ativo encontrado para este tipo de autorização.");
        } else {
          setChecklist(data);
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || "Erro ao carregar checklist.");
      } finally {
        if (!cancelled) setLoadingChecklist(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tipoAutorizacao]);

  const carregarPdfLayout = useCallback(async () => {
    try {
      const res = await pdfLayoutService.obterLatest();
      const layoutId = res.data?.data;

      const toDataUrl = (blob) =>
        new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });

      const [headerRes, footerRes, assinaturaRes] = await Promise.all([
        layoutId ? pdfLayoutService.getHeaderImage(layoutId).catch(() => null) : Promise.resolve(null),
        layoutId ? pdfLayoutService.getFooterImage(layoutId).catch(() => null) : Promise.resolve(null),
        autorizacoesService.getAssinaturaPadrao().catch(() => null),
      ]);

      setHeaderImgSrc(headerRes ? await toDataUrl(headerRes.data) : licencaHeaderImg);
      setFooterImgSrc(footerRes ? await toDataUrl(footerRes.data) : licencaFooterImg);

      const assinatura = await parseAssinaturaSecretarioResponse(assinaturaRes);
      if (assinatura.cadastrada) {
        setAssinaturaDataUrl(assinatura.dataUrl);
        setAssinaturaMensagem(null);
      } else {
        setAssinaturaDataUrl(null);
        setAssinaturaMensagem(assinatura.message);
      }
    } catch {
      setHeaderImgSrc(licencaHeaderImg);
      setFooterImgSrc(licencaFooterImg);
    }
  }, []);

  useEffect(() => {
    carregarPdfLayout();
    setUsuarioAtual(authService.getCurrentUser());
  }, [carregarPdfLayout]);

  // Validações de avanço
  const podeAvancarStep0 = tipoAutorizacao !== "" && checklist && !loadingChecklist;

  const podeAvancarStep1 =
    form.nomeEvento.trim() &&
    form.nomeEvento.length <= NOME_MAX &&
    form.dataEvento &&
    form.dataEvento >= hoje &&
    form.horaEvento &&
    form.localEvento.trim() &&
    (form.observacoes?.length ?? 0) <= OBS_MAX;

  const podeAvancarStep2 = useMemo(
    () =>
      (checklist?.itens ?? []).every(
        (it) => !it.obrigatorio || !!documentosFiles[`checklist_doc_${it.id}`]
      ),
    [checklist, documentosFiles]
  );

  const cpfCnpjDigits = cpf.replace(/\D/g, "");
  const podeSubmit =
    nomeRazaoSocial.trim() &&
    (cpfCnpjDigits.length === 11 || cpfCnpjDigits.length === 14) &&
    validateCpfCnpj(cpfCnpjDigits) &&
    endereco.trim() &&
    ehNumeroInformadoValido(numeroInformado);

  let prefixoProcesso = "";
  try {
    if (tipoAutorizacao !== "") {
      prefixoProcesso = obterCodigoTipoAutorizacao(Number(tipoAutorizacao));
    }
  } catch {
    prefixoProcesso = "";
  }
  const numeroProcessoExibicao = montarNumeroProcessoPreview(
    prefixoProcesso,
    numeroInformado,
    anoProcesso,
  );

  const handleNext = () => {
    setError("");
    if (activeStep === 0 && !podeAvancarStep0) return;
    if (activeStep === 1 && !podeAvancarStep1) {
      setError("Preencha todos os campos obrigatórios corretamente.");
      return;
    }
    if (activeStep === 2 && !podeAvancarStep2) {
      const erros = {};
      (checklist?.itens ?? []).forEach((it) => {
        if (it.obrigatorio && !documentosFiles[`checklist_doc_${it.id}`]) {
          erros[`checklist_doc_${it.id}`] = "Documento obrigatório não enviado";
        }
      });
      setDocErros(erros);
      setError("Envie todos os documentos obrigatórios antes de continuar.");
      return;
    }
    setActiveStep((s) => s + 1);
  };

  const handleBack = () => {
    setError("");
    setActiveStep((s) => s - 1);
  };

  const handleCpfChange = (e) => {
    const formatted = formatCpfCnpj(e.target.value);
    setCpf(formatted);
    setCpfErro("");
  };

  const handleCpfBlur = () => {
    const digits = cpf.replace(/\D/g, "");
    if (digits.length > 0 && digits.length !== 11 && digits.length !== 14) {
      setCpfErro("CPF/CNPJ incompleto.");
    } else if (
      (digits.length === 11 || digits.length === 14) &&
      !validateCpfCnpj(digits)
    ) {
      setCpfErro("CPF/CNPJ inválido.");
    } else {
      setCpfErro("");
    }
  };

  const gerarPdfBlob = async (autorizacaoId) => {
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = PDF_PAGE_WIDTH_MM;
    const pageHeight = PDF_PAGE_HEIGHT_MM;

    const SIG_DATE_X_MM = 10;
    const SIG_DATE_W_MM = 60;
    const SIG_BOX_X_MM = 72;
    const SIG_BOX_Y_TOP_MM = 242;
    const SIG_BOX_W_MM = 128;
    const SIG_BOX_H_MM = 25;
    const SIG_LABEL_HEIGHT_MM = 5;

    const headerCanvas = await html2canvasForPdf(
      html2canvas,
      headerRef.current,
    );
    const headerImg = canvasToJpegDataUrl(headerCanvas);
    const headerHeight = canvasHeightInMm(headerCanvas, pageWidth);

    const footerCanvas = await html2canvasForPdf(
      html2canvas,
      footerRef.current,
    );
    const footerImg = canvasToJpegDataUrl(footerCanvas);
    const footerHeight = canvasHeightInMm(footerCanvas, pageWidth);

    const contentAreaHeightPreview = pageHeight - headerHeight - footerHeight;

    let tabelaMarginAdded = false;
    if (tabelaRef.current && contentRef.current) {
      const contentRect = contentRef.current.getBoundingClientRect();
      const tableRect = tabelaRef.current.getBoundingClientRect();
      const pxToMm = pageWidth / contentRect.width;
      const tableTopMm = (tableRect.top - contentRect.top) * pxToMm;
      const tableHeightMm = tableRect.height * pxToMm;
      const tableBottomMm = tableTopMm + tableHeightMm;
      const tableStartPage = Math.floor(tableTopMm / contentAreaHeightPreview);
      const pageBreakMm = (tableStartPage + 1) * contentAreaHeightPreview;
      if (tableBottomMm > pageBreakMm) {
        const extraMarginMm = pageBreakMm - tableTopMm;
        tabelaRef.current.style.marginTop = `${extraMarginMm / pxToMm}px`;
        tabelaMarginAdded = true;
      }
    }

    const qrPrevDisplay = qrRef.current ? qrRef.current.style.display : "";
    if (qrRef.current) qrRef.current.style.display = "none";

    const contentCanvas = await html2canvasForPdf(
      html2canvas,
      contentRef.current,
    );

    if (qrRef.current) qrRef.current.style.display = qrPrevDisplay;

    let qrImg = null;
    let qrWidthMm = 0;
    let qrHeightMm = 0;
    if (qrRef.current && contentRef.current) {
      const qrCanvas = await html2canvasForPdf(html2canvas, qrRef.current, {
        backgroundColor: "#ffffff",
      });
      qrImg = canvasToJpegDataUrl(qrCanvas);
      const contentRect = contentRef.current.getBoundingClientRect();
      const mmPerDomPx = pageWidth / contentRect.width;
      const qrDomRect = qrRef.current.getBoundingClientRect();
      qrWidthMm = qrDomRect.width * mmPerDomPx;
      qrHeightMm = qrDomRect.height * mmPerDomPx;
    }

    if (tabelaMarginAdded) {
      tabelaRef.current.style.marginTop = "";
    }

    const paging = getContentPaging(
      contentCanvas,
      headerHeight,
      footerHeight,
      pageWidth,
      pageHeight,
      { signatureBoxYTopMm: SIG_BOX_Y_TOP_MM },
    );
    const totalContentPages = paging.totalPages;

    const lastContentPageIndex = totalContentPages - 1;
    const contentBottomYOnLastPage =
      headerHeight + paging.lastSliceHeightMm;
    const maxYOnPage = pageHeight - footerHeight;
    const qrTopMargin = 2;
    let qrPageIndex = lastContentPageIndex;
    let qrTargetY = contentBottomYOnLastPage + qrTopMargin;
    if (qrImg && qrTargetY + qrHeightMm > maxYOnPage) {
      qrPageIndex = totalContentPages;
      qrTargetY = headerHeight + qrTopMargin;
    }
    const totalPages = qrImg
      ? Math.max(totalContentPages, qrPageIndex + 1)
      : totalContentPages;

    for (let page = 0; page < totalPages; page++) {
      if (page > 0) pdf.addPage();
      if (page < totalContentPages) {
        addSlicedContentImage(
          pdf,
          contentCanvas,
          page,
          paging,
          pageWidth,
          headerHeight,
        );
      }
      pdf.addImage(headerImg, "JPEG", 0, 0, pageWidth, headerHeight);
      pdf.addImage(
        footerImg,
        "JPEG",
        0,
        pageHeight - footerHeight,
        pageWidth,
        footerHeight,
      );
      if (qrImg && page === qrPageIndex) {
        const qrXMm = pageWidth - qrWidthMm - 10;
        pdf.addImage(qrImg, "JPEG", qrXMm, qrTargetY, qrWidthMm, qrHeightMm);
      }
    }

    const dataAtualFormatada = formatDateToShow(new Date().toISOString());
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.2);
    pdf.setTextColor(0, 0, 0);
    pdf.setFillColor(255, 255, 255);
    pdf.rect(SIG_DATE_X_MM, SIG_BOX_Y_TOP_MM, SIG_DATE_W_MM, SIG_BOX_H_MM, "F");
    pdf.rect(SIG_BOX_X_MM, SIG_BOX_Y_TOP_MM, SIG_BOX_W_MM, SIG_BOX_H_MM, "F");

    pdf.rect(SIG_DATE_X_MM, SIG_BOX_Y_TOP_MM, SIG_DATE_W_MM, SIG_BOX_H_MM);
    pdf.setFontSize(8);
    pdf.text("Data", SIG_DATE_X_MM + SIG_DATE_W_MM / 2, SIG_BOX_Y_TOP_MM + 5, { align: "center" });
    pdf.setFontSize(10);
    pdf.text(dataAtualFormatada, SIG_DATE_X_MM + SIG_DATE_W_MM / 2, SIG_BOX_Y_TOP_MM + SIG_BOX_H_MM / 2 + 3, { align: "center" });

    pdf.rect(SIG_BOX_X_MM, SIG_BOX_Y_TOP_MM, SIG_BOX_W_MM, SIG_BOX_H_MM);
    pdf.setFontSize(8);
    pdf.text("Assinatura Digital:", SIG_BOX_X_MM + 2, SIG_BOX_Y_TOP_MM + SIG_LABEL_HEIGHT_MM - 1);
    if (assinaturaDataUrl) {
      const sigImgX = SIG_BOX_X_MM + 2;
      const sigImgY = SIG_BOX_Y_TOP_MM + SIG_LABEL_HEIGHT_MM;
      const sigImgW = SIG_BOX_W_MM - 4;
      const sigImgH = SIG_BOX_H_MM - SIG_LABEL_HEIGHT_MM - 1;
      pdf.addImage(
        assinaturaDataUrl,
        resolverFormatoImagemAssinatura(assinaturaDataUrl),
        sigImgX,
        sigImgY,
        sigImgW,
        sigImgH
      );
    } else if (assinaturaMensagem) {
      pdf.setFontSize(7);
      const lines = pdf.splitTextToSize(assinaturaMensagem, SIG_BOX_W_MM - 4);
      pdf.text(lines, SIG_BOX_X_MM + 2, SIG_BOX_Y_TOP_MM + SIG_LABEL_HEIGHT_MM + 4);
    }

    const dataAtual = new Date().toISOString().split("T")[0];
    const nomeArquivo = `Autorizacao-Independente-${dataAtual}.pdf`;
    const pdfBlob = pdf.output("blob");
    return new File([pdfBlob], nomeArquivo, { type: "application/pdf" });
  };

  const handleSubmit = async () => {
    if (!podeSubmit) {
      setError("Preencha todos os campos obrigatórios corretamente.");
      return;
    }
    if (!ehNumeroInformadoValido(numeroInformado)) {
      setError("Informe um número de processo válido (apenas dígitos).");
      return;
    }

    try {
      setEmitindo(true);
      setError("");

      const solicitacaoId = crypto.randomUUID();

      flushSync(() => {
        setSolicitacaoIdParaPdf(solicitacaoId);
      });

      const pdfFile = await gerarPdfBlob(solicitacaoId);

      const formData = new FormData();
      formData.append("solicitacaoId", solicitacaoId);
      formData.append("tipoAutorizacao", tipoAutorizacao);
      formData.append("nomeEvento", form.nomeEvento);
      formData.append("dataEvento", `${form.dataEvento}T${form.horaEvento}:00`);
      formData.append("localEvento", form.localEvento);
      if (form.observacoes) formData.append("observacoes", form.observacoes.replace(/\r\n|\r/g, "\n"));
      formData.append("nomeRazaoSocial", nomeRazaoSocial);
      formData.append("cpf", cpf.replace(/\D/g, ""));
      formData.append("endereco", endereco);
      if (condicionantes) formData.append("condicionantes", condicionantes.replace(/\r\n|\r/g, "\n"));
      if (observacoesEmissao) formData.append("observacoesEmissao", observacoesEmissao.replace(/\r\n|\r/g, "\n"));
      formData.append("numeroInformado", numeroInformado.trim());
      formData.append("arquivo", pdfFile);

      const result = await autorizacoesService.criarAutorizacaoIndependente(formData);

      // Upload dos documentos do checklist em paralelo
      const uploads = Object.entries(documentosFiles)
        .filter(([, arquivo]) => Boolean(arquivo))
        .map(([key, arquivo]) => {
          if (!solicitacaoId) return Promise.resolve();
          const itemId = key.replace("checklist_doc_", "");
          const item = checklist?.itens?.find((it) => String(it.id) === itemId);
          const descricao = item?.descricao || arquivo.name;
          const extensao = arquivo.name.split(".").pop();
          const novoNome = `${descricao}.${extensao}`;
          const arquivoRenomeado = new File([arquivo], novoNome, { type: arquivo.type });
          return autorizacoesService.uploadDocumentoAutorizacao(solicitacaoId, arquivoRenomeado);
        });
      await Promise.allSettled(uploads);

      setNovaAutorizacaoId(result?.data?.solicitacaoDeAutorizacaoId ?? solicitacaoId);
      setSucesso(true);
    } catch (err) {
      setError(getApiErrorMessage(err, "Erro ao criar e emitir a autorização."));
    } finally {
      setEmitindo(false);
    }
  };

  const cpfFormatadoPrevia = cpf || "___.___.___-__ / __.___.___/____-__";
  const enderecoPrevia = endereco || "____________________";

  if (sucesso) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 8, textAlign: "center" }}>
          <Paper
            elevation={0}
            sx={{
              p: 5,
              backgroundColor: alpha(theme.palette.success.main, 0.08),
              border: "2px solid",
              borderColor: "success.main",
              borderRadius: 2,
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 80, color: "success.main", mb: 2 }} />
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
              Autorização Criada e Emitida!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              A autorização independente foi criada e emitida com sucesso.
            </Typography>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
              {novaAutorizacaoId && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate(`/autorizacao/${novaAutorizacaoId}`)}
                >
                  Ver Detalhes
                </Button>
              )}
              <Button variant="outlined" onClick={() => navigate(-1)}>
                Voltar
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="lg"
      sx={{
        py: { xs: 2, md: 4 },
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      {/* Hidden PDF layout section */}
      <Box
        sx={{
          position: "absolute",
          left: "-9999px",
          top: 0,
          width: "800px",
        }}
      >
        <Box
          ref={headerRef}
          sx={{ backgroundColor: "white", overflow: "hidden", height: 80 }}
        >
          {headerImgSrc && (
            <img
              src={headerImgSrc}
              alt="Cabeçalho"
              style={{ width: "100%", height: "80px", objectFit: "fill", display: "block" }}
            />
          )}
        </Box>

        <Box
          ref={contentRef}
          sx={{
            backgroundColor: "#ffffff",
            p: 3,
            fontSize: "11px",
            lineHeight: 1.6,
            fontFamily: "Arial, sans-serif",
          }}
        >
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={8}>
              <Box
                sx={{
                  border: "1px solid #000",
                  p: 1,
                  textAlign: "center",
                  height: 80,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "15px",
                    fontWeight: "bold",
                    color: "black",
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                  }}
                >
                  Autorização - {tipoAutorizacaoLabel(Number(tipoAutorizacao))}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box
                sx={{
                  border: "1px solid #000",
                  p: 1,
                  textAlign: "center",
                  height: 80,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography sx={{ fontSize: "10px", fontWeight: "bold" }}>Processo:</Typography>
                <Typography sx={{ fontSize: "10px", fontWeight: "bold", wordBreak: "break-all" }}>
                  {numeroProcessoExibicao || "—"}
                </Typography>
                <Typography sx={{ fontSize: "9px", mt: 0.5 }}>
                  Emissão: {formatDateToShow(new Date().toISOString())}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: "bold", fontSize: "11px", mb: 0.5 }}>
              Identificação do Interessado
            </Typography>
            <Box sx={{ border: "1px solid #000", p: 1.5 }}>
              <Typography sx={{ fontSize: "11px", mb: 0.5 }}>
                <strong style={{ fontSize: "11px" }}>Nome / Razão Social:</strong>{" "}
                {nomeRazaoSocial || "____________________"}
              </Typography>
              <Typography sx={{ fontSize: "11px", mb: 0.5 }}>
                <strong style={{ fontSize: "11px" }}>CPF/CNPJ:</strong> {cpfFormatadoPrevia}
              </Typography>
              <Typography sx={{ fontSize: "11px" }}>
                <strong style={{ fontSize: "11px" }}>Endereço:</strong> {enderecoPrevia}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: "bold", fontSize: "11px", mb: 0.5 }}>
              Dados do Evento
            </Typography>
            <Box sx={{ border: "1px solid #000", p: 1.5 }}>
              <Typography sx={{ fontSize: "11px", mb: 0.5 }}>
                <strong style={{ fontSize: "11px" }}>Nome do Evento:</strong>{" "}
                {form.nomeEvento || "____________________"}
              </Typography>
              <Typography sx={{ fontSize: "11px", mb: 0.5 }}>
                <strong style={{ fontSize: "11px" }}>Dia(s) do Evento:</strong>{" "}
                {form.dataEvento
                  ? new Date(form.dataEvento + "T00:00").toLocaleDateString("pt-BR")
                  : "____________________"}
                {horaInicio && horaFim
                  ? ` (das ${horaInicio} às ${horaFim} hrs)`
                  : horaInicio
                  ? ` (a partir das ${horaInicio} hrs)`
                  : ""}
              </Typography>
              <Typography sx={{ fontSize: "11px", mb: 0.5 }}>
                <strong style={{ fontSize: "11px" }}>Local do Evento:</strong>{" "}
                {form.localEvento || "____________________"}
              </Typography>
              <Typography sx={{ fontSize: "11px", whiteSpace: "pre-wrap" }}>
                <strong style={{ fontSize: "11px" }}>Observações:</strong>{" "}
                {observacoesEmissao || "Nenhuma observação."}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              border: "2px solid #000",
              p: 1.5,
              mb: 2,
              backgroundColor: "#fff8e1",
            }}
          >
            <Typography
              sx={{ fontWeight: "bold", fontSize: "11px", mb: 0.5, textAlign: "center" }}
            >
              IMPORTANTE
            </Typography>
            <Typography sx={{ fontSize: "10px", textAlign: "justify", fontWeight: "bold" }}>
              {IMPORTANTE_TEXTO}
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: "bold", fontSize: "11px", mb: 0.5 }}>
              CONDICIONANTES
            </Typography>
            <Box
              sx={{
                border: "1px solid #000",
                p: 1.5,
                minHeight: 60,
                whiteSpace: "pre-wrap",
              }}
            >
              <Typography sx={{ fontSize: "11px", whiteSpace: "pre-wrap" }}>
                {condicionantes || "Nenhuma condicionante informada."}
              </Typography>
            </Box>
          </Box>

          <Box ref={tabelaRef} sx={{ mb: 2 }}>
            <Typography
              sx={{ fontWeight: "bold", fontSize: "11px", mb: 0.5, textAlign: "center" }}
            >
              Limites de níveis de pressão sonora, segundo a NBR 10.151 / ABNT
            </Typography>
            <TableContainer component={Box} sx={{ border: "1px solid #000" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontSize: "10px",
                        fontWeight: "bold",
                        border: "1px solid #000",
                        backgroundColor: "#f0f0f0",
                      }}
                    >
                      Tipos de Áreas
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: "10px",
                        fontWeight: "bold",
                        border: "1px solid #000",
                        backgroundColor: "#f0f0f0",
                      }}
                    >
                      Diurno (dB)
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: "10px",
                        fontWeight: "bold",
                        border: "1px solid #000",
                        backgroundColor: "#f0f0f0",
                      }}
                    >
                      Noturno (dB)
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {NIVEIS_PRESSAO_SONORA.map((row) => (
                    <TableRow key={row.categoria}>
                      <TableCell sx={{ fontSize: "10px", border: "1px solid #000" }}>
                        {row.categoria}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontSize: "10px", border: "1px solid #000" }}
                      >
                        {row.diurno}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontSize: "10px", border: "1px solid #000" }}
                      >
                        {row.noturno}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box ref={qrRef} sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <QRCodeSVG
                value={`${window.location.origin}/validar/autorizacao/${solicitacaoIdParaPdf || "preview"}`}
                size={70}
                level="M"
              />
              <Typography sx={{ fontSize: "8px", mt: 0.5, textAlign: "center" }}>
                Autenticidade
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          ref={footerRef}
          sx={{ backgroundColor: "white", overflow: "hidden", height: 60 }}
        >
          {footerImgSrc && (
            <img
              src={footerImgSrc}
              alt="Rodapé"
              style={{ width: "100%", height: "60px", objectFit: "fill", display: "block" }}
            />
          )}
        </Box>
      </Box>

      {/* Visible page content */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          mb: { xs: 3, md: 4 },
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <AssignmentTurnedInIcon
          sx={{
            fontSize: { xs: 28, md: 36 },
            color: theme.palette.primary.main,
            mr: { xs: 1, md: 2 },
          }}
        />
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              lineHeight: 1.1,
              fontSize: { xs: "1.35rem", sm: "1.75rem", md: "2.125rem" },
            }}
          >
            Nova Autorização Independente
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Crie e emita uma autorização diretamente, sem necessidade de solicitação prévia do interessado
          </Typography>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          mb: 3,
          border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          borderRadius: 2,
          overflowX: "auto",
        }}
      >
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{
            minWidth: { xs: 480, sm: "auto" },
            "& .MuiStepLabel-label": {
              fontSize: { xs: "0.7rem", sm: "0.875rem" },
            },
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          borderRadius: 2,
        }}
      >
        {/* Step 0 — Tipo de Autorização */}
        {activeStep === 0 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              Selecione o tipo de autorização
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Escolha o tipo adequado para o evento. O checklist de documentação será carregado automaticamente.
            </Typography>
            <Grid container spacing={2}>
              {tipoAutorizacaoEnum.map((t) => {
                const selected = String(tipoAutorizacao) === String(t.value);
                return (
                  <Grid item xs={12} sm={6} key={t.value}>
                    <Card
                      elevation={0}
                      sx={{
                        border: `2px solid ${
                          selected
                            ? theme.palette.primary.main
                            : alpha(theme.palette.divider, 0.8)
                        }`,
                        borderRadius: 2,
                        backgroundColor: selected
                          ? alpha(theme.palette.primary.main, 0.05)
                          : "transparent",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          borderColor: theme.palette.primary.main,
                          backgroundColor: alpha(theme.palette.primary.main, 0.03),
                        },
                      }}
                    >
                      <CardActionArea onClick={() => setTipoAutorizacao(t.value)} sx={{ p: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Box
                            sx={{
                              color: selected ? theme.palette.primary.main : "text.secondary",
                              display: "flex",
                            }}
                          >
                            {TIPO_ICONS[t.value] ?? <EventIcon sx={{ fontSize: 32 }} />}
                          </Box>
                          <Box>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 600,
                                color: selected ? theme.palette.primary.main : "text.primary",
                              }}
                            >
                              {t.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {TIPO_DESC[t.value] ?? ""}
                            </Typography>
                          </Box>
                          {selected && (
                            <CheckCircleIcon
                              sx={{ ml: "auto", color: theme.palette.primary.main }}
                            />
                          )}
                        </Box>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {Number(tipoAutorizacao) === TIPO_USO_FOGO_CONTROLADO && (
              <Alert severity="info" sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Potencial poluidor-degradador: {CLASSIFICACAO_USO_FOGO_CONTROLADO.potencial}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  Classificação por {CLASSIFICACAO_USO_FOGO_CONTROLADO.unidade} (referência
                  — não altera o formulário):
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Porte</TableCell>
                        <TableCell>Faixa</TableCell>
                        <TableCell>Código</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {CLASSIFICACAO_USO_FOGO_CONTROLADO.faixas.map((faixa) => (
                        <TableRow key={faixa.codigo}>
                          <TableCell>{faixa.porte}</TableCell>
                          <TableCell>{faixa.descricao}</TableCell>
                          <TableCell>
                            <strong>{faixa.codigo}</strong>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Alert>
            )}

            {loadingChecklist && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 3 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Carregando checklist...
                </Typography>
              </Box>
            )}

            {checklist && !loadingChecklist && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.success.main, 0.06),
                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: theme.palette.success.dark }}
                  >
                    Checklist encontrado: {checklist.nome}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {checklist.itens?.length || 0} documento(s) exigido(s)
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Step 1 — Dados do Evento */}
        {activeStep === 1 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              Dados do Evento
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Preencha as informações do evento para o qual a autorização será emitida.
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="Nome do Evento"
                  fullWidth
                  required
                  value={form.nomeEvento}
                  inputProps={{ maxLength: NOME_MAX }}
                  onChange={(e) => setForm({ ...form, nomeEvento: e.target.value })}
                  helperText="Nome curto e descritivo do evento"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CharCounter value={form.nomeEvento} maxLength={NOME_MAX} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Data do Evento"
                  type="date"
                  fullWidth
                  required
                  value={form.dataEvento}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: hoje }}
                  onChange={(e) => setForm({ ...form, dataEvento: e.target.value })}
                  helperText="A data não pode ser anterior a hoje"
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  label="Horário"
                  type="time"
                  fullWidth
                  required
                  value={form.horaEvento}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => setForm({ ...form, horaEvento: e.target.value })}
                  helperText="Hora de início"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Local do Evento"
                  fullWidth
                  required
                  value={form.localEvento}
                  onChange={(e) => setForm({ ...form, localEvento: e.target.value })}
                  helperText="Endereço ou nome do local"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Observações (opcional)"
                  fullWidth
                  multiline
                  rows={4}
                  value={form.observacoes}
                  inputProps={{ maxLength: OBS_MAX }}
                  onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
                  helperText="Informações adicionais sobre o evento"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CharCounter value={form.observacoes} maxLength={OBS_MAX} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Step 2 — Documentos */}
        {activeStep === 2 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              Documentação
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Anexe os documentos exigidos pelo checklist. Itens marcados como{" "}
              <strong>obrigatórios</strong> devem ser enviados para continuar.
            </Typography>
            <ChecklistDocumentosUpload
              itensChecklist={checklist?.itens ?? []}
              documentos={documentosFiles}
              documentosChecklistFiles={{}}
              documentosErros={docErros}
              onFileChange={(itemId, arquivo) => {
                setDocumentosFiles((prev) => ({
                  ...prev,
                  [`checklist_doc_${itemId}`]: arquivo,
                }));
                setDocErros((prev) => {
                  const next = { ...prev };
                  delete next[`checklist_doc_${itemId}`];
                  return next;
                });
              }}
            />
          </Box>
        )}

        {/* Step 3 — Identificação e Emissão */}
        {activeStep === 3 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              Identificação do Interessado e Emissão
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Informe os dados do interessado e configure as condicionantes da autorização.
            </Typography>

            <Grid container spacing={3}>
              {/* Identificação do Interessado */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Identificação do Interessado
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={12}>
                <NumeroProcessoField
                  prefixo={prefixoProcesso}
                  value={numeroInformado}
                  onChange={setNumeroInformado}
                  ano={anoProcesso}
                  error={Boolean(numeroInformado) && !ehNumeroInformadoValido(numeroInformado)}
                  helperText={
                    Boolean(numeroInformado) && !ehNumeroInformadoValido(numeroInformado)
                      ? "Informe apenas dígitos (1 a 10)."
                      : undefined
                  }
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  label="Nome / Razão Social"
                  fullWidth
                  required
                  value={nomeRazaoSocial}
                  inputProps={{ maxLength: NOME_INTERESSADO_MAX }}
                  onChange={(e) => setNomeRazaoSocial(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CharCounter value={nomeRazaoSocial} maxLength={NOME_INTERESSADO_MAX} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="CPF/CNPJ"
                  fullWidth
                  required
                  value={cpf}
                  onChange={handleCpfChange}
                  onBlur={handleCpfBlur}
                  placeholder="000.000.000-00 ou 00.000.000/0000-00"
                  error={Boolean(cpfErro)}
                  helperText={cpfErro || ""}
                  inputProps={{ maxLength: 18 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Endereço"
                  fullWidth
                  required
                  value={endereco}
                  inputProps={{ maxLength: ENDERECO_MAX }}
                  onChange={(e) => setEndereco(e.target.value)}
                  placeholder="Ex: Av. João Pereira Torres, TAMBORIL, CEP 63765-000"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CharCounter value={endereco} maxLength={ENDERECO_MAX} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Hora da Vigência */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, mt: 1 }}>
                  Hora da Vigência
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Hora início"
                  type="time"
                  fullWidth
                  size="small"
                  value={horaInicio}
                  onChange={(e) => setHoraInicio(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  label="Hora fim"
                  type="time"
                  fullWidth
                  size="small"
                  value={horaFim}
                  onChange={(e) => setHoraFim(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Observações da Emissão */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, mt: 1 }}>
                  Observações da Emissão
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  multiline
                  rows={4}
                  fullWidth
                  value={observacoesEmissao}
                  onChange={(e) => setObservacoesEmissao(e.target.value)}
                  placeholder="Insira as observações desta autorização..."
                  variant="outlined"
                  inputProps={{ maxLength: OBSERVACOES_EMISSAO_MAX }}
                  InputProps={{
                    endAdornment: (
                      <CharCounter value={observacoesEmissao} maxLength={OBSERVACOES_EMISSAO_MAX} />
                    ),
                  }}
                />
              </Grid>

              {/* Condicionantes */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, mt: 1 }}>
                  Condicionantes
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  multiline
                  rows={8}
                  fullWidth
                  value={condicionantes}
                  onChange={(e) => setCondicionantes(e.target.value)}
                  placeholder="Descreva as condicionantes desta autorização..."
                  variant="outlined"
                  inputProps={{ maxLength: CONDICIONANTES_MAX }}
                  InputProps={{
                    endAdornment: (
                      <CharCounter value={condicionantes} maxLength={CONDICIONANTES_MAX} />
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Navigation */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mt: 4 }}
        >
          <Button
            onClick={activeStep === 0 ? () => navigate(-1) : handleBack}
            disabled={emitindo}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            {activeStep === 0 ? "Cancelar" : "Voltar"}
          </Button>

          {activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={
                (activeStep === 0 && !podeAvancarStep0) ||
                (activeStep === 1 && !podeAvancarStep1) ||
                (activeStep === 2 && !podeAvancarStep2)
              }
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              Próximo
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              onClick={handleSubmit}
              disabled={emitindo || !podeSubmit}
              sx={{ minWidth: { xs: "100%", sm: 200 } }}
            >
              {emitindo ? <CircularProgress size={22} color="inherit" /> : "Criar e Emitir"}
            </Button>
          )}
        </Stack>
      </Paper>
    </Container>
  );
}

export default CriarAutorizacaoIndependente;
